import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import { get } from "lodash";
import PropTypes from "prop-types";

import Tooltip from "Components/Common/Tooltip";
import {
  CONSUMER,
  SUPER_ADMIN,
  applicationStatusTypes,
  dcsAdmin3Roles,
  dcsAdmin4Roles,
  otherUtilitiesAdmin3Roles,
  otherUtilitiesAdmin4Roles,
  powerAdmin3Roles,
  powerAdmin4Roles,
} from "common/constant";
import { isAuthorizedUser } from "utils";
import { getOptionLabel } from "common/enums";
import GSTNumberEditField from "./GSTNumberEditField";
import PanNumberEditField from "./PanNumberEditFiled";
import Collapse from "./Collapse";

import { useApplicantTypeQuery } from "apis/ApplicantType";
import AlternateEmailEditField from "./AlternateEmailEditField";

const authorizedRolesToUpdatePanAndGSTNumber = [
  SUPER_ADMIN,
  ...powerAdmin3Roles,
  ...powerAdmin4Roles,
  ...dcsAdmin3Roles,
  ...dcsAdmin4Roles,
  ...otherUtilitiesAdmin3Roles,
  ...otherUtilitiesAdmin4Roles,
];

const mapBooleanToYesNo = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return "-";
};

const PersonalDetailsView = ({
  applicant,
  applicationStatusId,
  applicationId,
  isExpandable = true,
  isApplicationViewModal = false,
  isAlternateEmailEdit = false,
  t,
}) => {
  const { data: { data: { items: applicantType } = {} } = {} } =
    useApplicantTypeQuery();

  const { roles } = useSelector((state) => state.Login);

  const [isGSTNumberEditModalOpen, setIsGSTNumberEditModalOpen] =
    useState(false);
  const [isPanNumberEditModalOpen, setIsPanNumberEditModalOpen] =
    useState(false);
  const [isAlternateEmailEditModalOpen, setIsAlternateEmailEditModalOpen] =
    useState(false);

  const isIndividualApplicantType = useMemo(
    () =>
      applicantType?.find(
        (application) => application.name.toLowerCase() === "individual"
      )?.id === applicant?.applicantType?.id,
    [applicantType, applicant?.applicantType?.id]
  );

  const isGSTAndPanEditable = useMemo(
    () =>
      (isAuthorizedUser(roles, authorizedRolesToUpdatePanAndGSTNumber) ||
        (roles.includes(CONSUMER) &&
          applicationStatusId === applicationStatusTypes["Reviewed"] &&
          isApplicationViewModal)) &&
      !!applicationId,
    [applicationStatusId, isApplicationViewModal, roles, applicationId]
  );

  const fields = useMemo(
    () => [
      {
        name: "Applicant Type",
        key: "applicantType.name",
        isRequired: true,
      },
      {
        name: "Name",
        key: "applicantName",
        isRequired: true,
      },

      ...(applicant?.oldApplicantName
        ? [
            {
              name: "Old Name",
              key: "oldApplicantName",
              isRequired: false,
            },
          ]
        : []),
      {
        name: "Occupancy Type",
        key: "occupancyType.name",
        isRequired: true,
      },
      {
        name: "Email",
        key: "email",
        isRequired: true,
      },
      {
        name: "Alternate Email",
        key: "alternateEmail",
        isRequired: false,
        renderField: (value) => (
          <>
            {value || "N/A"}
            {roles.includes(CONSUMER) && isAlternateEmailEdit && (
              <IconButton
                sx={{ padding: "5px" }}
                onClick={() => setIsAlternateEmailEditModalOpen(true)}
              >
                <Tooltip title={t("Change Alternate Email")}>
                  <EditIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}
          </>
        ),
      },
      {
        name: "Mobile Number",
        key: "mobileNumber",
        isRequired: true,
      },
      {
        name: "Alternate Mobile Number",
        key: "alternateMobileNumber",
        isRequired: false,
      },
      {
        name: "Land Line",
        key: "landLine",
        isRequired: false,
      },
      {
        name: "GST Number",
        key: "gstNumber",
        renderField: (value) => (
          <>
            {value || "Unregistered"}

            {isGSTAndPanEditable && (
              <IconButton
                sx={{ padding: "5px" }}
                onClick={() => setIsGSTNumberEditModalOpen(true)}
              >
                <Tooltip title={t("Change GST Number")}>
                  <EditIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}
          </>
        ),
      },
      {
        name: "PAN Number",
        key: "panNumber",
        renderField: (value) => (
          <>
            {value ? value : isIndividualApplicantType ? "N/A" : "-"}

            {isGSTAndPanEditable && (
              <IconButton
                sx={{ padding: "5px" }}
                onClick={() => setIsPanNumberEditModalOpen(true)}
              >
                <Tooltip title={t("Change Pan Number")}>
                  <EditIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}
          </>
        ),
      },
      {
        name: "Address Type",
        key: "addressType",
        isRequired: true,
        renderField: (value) => getOptionLabel("addressOptions", value) || "-",
      },
      {
        name: "Correspondence Address",
        key: "correspondenceAddress",
        isRequired: true,
      },
      {
        name: "Pincode",
        key: "pinCode",
        isRequired: true,
      },
      {
        name: "City",
        key: "city.name",
        isRequired: true,
      },
      {
        name: "E-Bill Confirmation",
        key: "eBillConfirmation",
        isRequired: true,
        renderField: (value) => mapBooleanToYesNo(value),
      },
    ],
    [isIndividualApplicantType, applicationStatusId, isGSTAndPanEditable, roles]
  );

  return (
    <>
      {isGSTNumberEditModalOpen && (
        <GSTNumberEditField
          applicationData={{
            applicationId,
            gstNumber: applicant.gstNumber ?? "",
            panNumber: applicant.panNumber ?? "",
            isGSTRegistered: applicant.isGSTRegistered,
          }}
          handleClose={() => setIsGSTNumberEditModalOpen(false)}
        />
      )}

      {isPanNumberEditModalOpen && (
        <PanNumberEditField
          applicationData={{
            applicationId,
            gstNumber: applicant.gstNumber ?? "",
            panNumber: applicant.panNumber ?? "",
            isIndividualApplicantType,
          }}
          handleClose={() => setIsPanNumberEditModalOpen(false)}
        />
      )}

      {isAlternateEmailEditModalOpen && (
        <AlternateEmailEditField
          applicationData={{
            applicantId: applicant?.id ?? "",
            alternateEmail: applicant?.alternateEmail?.split(", ") ?? [],
          }}
          handleClose={() => setIsAlternateEmailEditModalOpen(false)}
        />
      )}

      <Collapse title="Personal Details" isExpandable={isExpandable}>
        <Row className="d-flex mb-2">
          {fields.map(({ name, key, isRequired, renderField }) => {
            const value = get(applicant, key, "");

            return (
              <Col key={key} lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t(name)}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {renderField
                      ? renderField(value)
                      : (get(applicant, key, null) ??
                        (isRequired ? "-" : "N/A"))}
                  </Col>
                </Row>
              </Col>
            );
          })}
        </Row>
      </Collapse>
    </>
  );
};

PersonalDetailsView.propTypes = {
  applicant: PropTypes.object,
  isExpandable: PropTypes.bool,
  applicationStatusId: PropTypes.number,
  isApplicationViewModal: PropTypes.bool,
  applicationId: PropTypes.number,
  isOtherApplication: PropTypes.bool,
  isAlternateEmailEdit: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(PersonalDetailsView);
